import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    BlobType,
    FileModel,
    ModuleCategoryV1Read,
    ModuleSubmissionV1,
    ModuleUpdate,
    ModuleV1Read,
    OptionItem,
    UploadFileItem,
    VersionReadModel,
} from '@ca/core/models';
import { UserNotificationService } from '@ca/core/services';
import { CustomValidators } from '@ca/utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Guid } from 'guid-typescript';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { MODULE_TITLE_PATTERN } from '../../constants/constants';
import { DESCRIPTION_LENGTH_LIMIT, SUMMARY_LENGTH_LIMIT, SUPPORTED_IMAGE_TYPES, TITLE_LENGTH_LIMIT } from '../../constants/form-validators';
import { PublishModalFunctions } from '../module-detail/module-detail.component';

type StepState = 1 | 2;

@Component({
    selector: 'ca-module-publish',
    templateUrl: './module-publish.component.html',
    styleUrls: ['./module-publish.component.scss'],
})
export class ModulePublishComponent implements OnInit, OnDestroy {
    @Input() categories: ModuleCategoryV1Read[] = [];
    @Input() compliances: OptionItem<string>[] = [];

    module: ModuleV1Read = new ModuleV1Read();
    formGroup: UntypedFormGroup;
    isSubmitting: boolean = false;
    functions: PublishModalFunctions;

    versionUnderEdit?: VersionReadModel;
    moduleImageBlobType: BlobType = BlobType.ModuleImage;
    testFileBlobType: BlobType = BlobType.TestFile;
    readonly supportedImageTypes: string[] = SUPPORTED_IMAGE_TYPES;
    readonly descriptionLengthLimit = DESCRIPTION_LENGTH_LIMIT;
    readonly summaryLengthLimit = SUMMARY_LENGTH_LIMIT;
    readonly titleLengthLimit = TITLE_LENGTH_LIMIT;

    currentCategory: string = '';
    testFileUrls: string[] = [];
    testFiles: FileModel[] = [];
    isNextButtonEnabled: boolean = false;

    makeOpenSource: boolean = false;
    isCompliant: boolean = false;
    step: StepState = 1;
    stepValidator: object = {
        step1: {
            start: 0,
            end: 6,
        },
        step2: {
            start: 7,
        },
    };

    private destroy$$: Subject<void> = new Subject<void>();

    // add form control names of first page here for validation
    private readonly formControlsOfFirstPage: Array<string> = ['imageUrl', 'title', 'summary', 'description', 'categoryId', 'isCompliant', 'makeOpenSource'];

    get description(): AbstractControl {
        return this.formGroup.get('description') as AbstractControl;
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
    ) {}

    ngOnInit(): void {
        const { categoryId, categoryName, title, draftVersion } = this.module;

        this.versionUnderEdit = draftVersion;

        if (categoryName) {
            this.currentCategory = categoryName;
        }

        const summary = draftVersion != null ? draftVersion.summary : undefined;
        const description = draftVersion != null ? draftVersion.description : undefined;
        const image = draftVersion != null ? draftVersion.image : undefined;

        this.formGroup = this.formBuilder.group({
            title: [title, Validators.pattern(MODULE_TITLE_PATTERN)],
            summary: [summary, CustomValidators.SummaryValidation()],
            description: [description, [CustomValidators.requiredWithoutHtmlTags(), CustomValidators.maxLengthWithoutHtmlTags(DESCRIPTION_LENGTH_LIMIT)]],
            imageUrl: [image, Validators.required],
            categoryId: [categoryId, Validators.required],
            makeOpenSource: [this.makeOpenSource, Validators.required],
            isCompliant: [this.isCompliant, Validators.required],
            testFileUrls: [this.testFileUrls, Validators.required],
            agreeProtocol: [false, Validators.requiredTrue],
        });

        // Initially, check if Next Button needs to be disabled/enabled
        this.checkIfNextButtonShouldBeEnabled();

        // On every change: Check if "Next" button should be enabled
        // We don't need to check second page here, because the save button is bound to formGroup.valid
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroy$$))
            .pipe(
                tap(() => {
                    this.checkIfNextButtonShouldBeEnabled();
                }),
            )
            .subscribe();
    }

    removeFileItem(fileItem): void {
        if (fileItem) {
            if (fileItem.remove) {
                fileItem.remove();
            } else {
                fileItem.parentNode.removeChild(fileItem);
            }

            this.formGroup.get('imageUrl')?.setValue(null);
        }
    }

    updateImageUrlState(invalidFiles: UploadFileItem[]): void {
        this.formGroup.get('imageUrl')?.setValue('');
        this.formGroup.get('imageUrl')?.markAsDirty({ onlySelf: true });
        if (invalidFiles[0].unsupportedFileTypeError) {
            this.formGroup.get('imageUrl')?.setErrors({ invalidImageType: 'Invalid image type.' });
        }
        if (invalidFiles[0].maxFileSizeError) {
            this.formGroup.get('imageUrl')?.setErrors({ maxFileSize: 'File size cannot be larger than 1MB.' });
        }
    }

    onUploadCompleted({ token }: FileModel): void {
        const imageUrl = token + '?refresh=' + Guid.create();
        this.formGroup.get('imageUrl')?.setValue(imageUrl);
        this.formGroup.get('imageUrl')?.markAsDirty({ onlySelf: true });
    }

    updateTestFileUrls(file: FileModel): void {
        this.testFiles.push(file);
        this.testFileUrls.push(file.token);
        this.updateTestFileFormControl();
    }

    removeTestFileUrl(url: string): void {
        this.testFileUrls = this.testFileUrls.filter((x: string) => x !== url);
        this.testFiles = this.testFiles.filter(f => f.token === url);
        this.updateTestFileFormControl();
    }

    onTouched(): void {
        this.description.markAsTouched();
    }

    submit(): void {
        if (this.formGroup.valid) {
            const { categoryId, title, description, summary, image, imageUrl, testFileUrls, isCompliant, makeOpenSource } = this.formGroup.value;

            // If there are no changes, there's no need to save.
            if (
                this.module.draftVersion != null &&
                title === this.module.title &&
                categoryId === this.module.categoryId &&
                description === this.module.draftVersion.description &&
                summary === this.module.draftVersion.summary &&
                image === this.module.draftVersion.image
            ) {
                return;
            }

            this.isSubmitting = true;

            const updateModel: ModuleUpdate = {
                id: this.module.id,
                moduleUpdates: {
                    title,
                    description,
                    summary,
                    categoryId,
                    image: imageUrl,
                    compliances: [],
                },
            };

            const submissionModel: ModuleSubmissionV1 = {
                moduleId: this.module.id,
                submissionData: {
                    commentToReviewer: '',
                    makeOpenSource,
                    testFileUrls,
                    compliances: isCompliant && this.compliances[0] != null ? [this.compliances[0].value] : [],
                },
            };

            this.userNotificationService.showSpinner();
            this.isSubmitting = true;
            this.functions.updateAndSubmitModule(updateModel, submissionModel);
        } else {
            // If the formGroup is not valid, mark all fields as touched to reveal errors in the UI
            this.formGroup.markAllAsTouched();
        }
    }

    goToStepOne(): void {
        this.step = 1;
    }

    /**
     * Checks if all formControls of step 1 are valid. If so, continues to second step.
     */
    nextStep(): void {
        const checkRange = this.stepValidator['step' + this.step];
        const controlsOfFirstStep: UntypedFormControl[] = Object.values(this.formGroup.controls).slice(
            checkRange.start,
            checkRange.end,
        ) as UntypedFormControl[];

        const areAllControlsValid: boolean = controlsOfFirstStep.every((control: UntypedFormControl) => {
            control.markAsDirty({ onlySelf: true });

            return control.valid;
        });

        if (areAllControlsValid) {
            this.step = 2;
        }
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }

    ngOnDestroy(): void {
        this.destroy$$.next();
        this.destroy$$.complete();
    }

    private checkIfNextButtonShouldBeEnabled(): void {
        this.isNextButtonEnabled = this.checkIfAllControlsAreValid(this.formControlsOfFirstPage);
    }

    private checkIfAllControlsAreValid(formControlNames: string[]): boolean {
        return formControlNames
            .map((formControlName: string) => {
                return this.formGroup.get(formControlName);
            })
            .every((control: UntypedFormControl) => control.valid);
    }

    /**
     * When we change the test file URLs, we add/remove it from this.testFileUrls.
     * However, this does not update formControl value and validity. Therefore we need to do it manually.
     */
    private updateTestFileFormControl(): void {
        const testFileUrlFormControl: UntypedFormControl | undefined = this.formGroup.get('testFileUrls') as UntypedFormControl | undefined;

        if (testFileUrlFormControl != null) {
            testFileUrlFormControl.setValue(this.testFileUrls);
            testFileUrlFormControl.markAsTouched();
            testFileUrlFormControl.updateValueAndValidity();
        }
    }
}
